<template>
  <div class="migration-wrapper">
    <v-card class="migration-card" elevation="8">
      <div class="migration-card__banner">
        <v-img
          alt="Foz"
          class="migration-card__logo"
          contain
          height="40"
          src="@/assets/logo.png"
        />
      </div>

      <v-card-text class="migration-card__body">
        <v-chip class="migration-card__chip" color="#E8F2FF" text-color="#1D8AFF" small>
          Nova plataforma Foz
        </v-chip>

        <h1 class="migration-card__title">{{ copy.title }}</h1>

        <p class="migration-card__text">
          As funcionalidades de telemetria
          <template v-if="keepsProjects">da Foz </template>agora estão
          disponíveis em nossa nova plataforma, desenvolvida para oferecer uma
          experiência <strong>mais rápida, moderna e segura</strong>.
        </p>

        <p v-if="keepsProjects" class="migration-card__text">
          Painel, consumo, alertas, unidades, níveis, metas, relatórios e demais
          funcionalidades relacionadas à telemetria devem ser acessados pela
          nova plataforma.
        </p>

        <template v-else>
          <p class="migration-card__text">
            Painel, consumo, alertas, unidades, níveis, metas, relatórios e
            configurações continuam disponíveis normalmente, com
            <strong>seus dados e permissões preservados</strong>.
          </p>

          <p class="migration-card__text">
            A nova plataforma também está preparada para receber continuamente
            novas funcionalidades e melhorias para facilitar ainda mais o
            acompanhamento da sua operação.
          </p>

          <ul class="migration-card__benefits">
            <li
              v-for="benefit in benefits"
              :key="benefit.label"
              class="migration-card__benefit"
            >
              <v-icon color="#1D8AFF" size="20">{{ benefit.icon }}</v-icon>
              <span>{{ benefit.label }}</span>
            </li>
          </ul>
        </template>

        <p class="migration-card__credentials">
          <v-icon class="mr-2" color="#1D8AFF" size="18">
            mdi-account-check-outline
          </v-icon>
          {{ copy.credentials }}
        </p>

        <v-btn
          block
          class="migration-card__cta"
          color="#1D8AFF"
          dark
          depressed
          large
          :href="newPlatformUrl"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ copy.cta }}
          <v-icon right small>mdi-open-in-new</v-icon>
        </v-btn>

        <a
          class="migration-card__link"
          :href="newPlatformUrl"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ newPlatformLabel }}
        </a>
      </v-card-text>

      <template v-if="keepsProjects">
        <v-divider />

        <div class="migration-card__stays">
          <h2 class="migration-card__stays-title">
            <v-icon class="mr-2" color="#2F855A" size="20">mdi-check-circle-outline</v-icon>
            Gestão de Projetos continua aqui
          </h2>

          <p class="migration-card__stays-text">
            Seu acesso ao módulo de <strong>Gestão de Projetos não mudou</strong>.
            Você deve continuar utilizando esta plataforma normalmente para
            acompanhar e gerenciar seus projetos.
          </p>

          <ul class="migration-card__split">
            <li class="migration-card__split-row">
              <span class="migration-card__split-from">Telemetria</span>
              <v-icon color="#9AA5B1" size="18">mdi-arrow-right</v-icon>
              <span class="migration-card__split-to migration-card__split-to--new">
                Nova plataforma Foz
              </span>
            </li>
            <li class="migration-card__split-row">
              <span class="migration-card__split-from">Gestão de Projetos</span>
              <v-icon color="#9AA5B1" size="18">mdi-arrow-right</v-icon>
              <span class="migration-card__split-to migration-card__split-to--here">
                Continua nesta plataforma
              </span>
            </li>
          </ul>
        </div>
      </template>

      <v-divider />

      <div class="migration-card__support">
        <h2 class="migration-card__support-title">Precisa de ajuda?</h2>

        <p class="migration-card__support-text">{{ copy.support }}</p>

        <p class="migration-card__support-phone">
          <v-icon class="mr-2" color="#52606D" size="18">mdi-phone-outline</v-icon>
          <strong>SAC Foz:</strong>&nbsp;{{ supportPhoneLabel }}
        </p>

        <v-btn
          class="migration-card__support-cta"
          color="#1D8AFF"
          :href="supportWhatsappUrl"
          outlined
          rel="noopener noreferrer"
          target="_blank"
        >
          <v-icon left small>mdi-whatsapp</v-icon>
          Falar com o SAC
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  hasLegacyFeature,
  NEW_PLATFORM_LABEL,
  NEW_PLATFORM_URL,
  SUPPORT_PHONE_LABEL,
  SUPPORT_WHATSAPP_URL,
} from "@/router/migratedFeatures.js";

export default {
  name: "MigrationNotice",

  data: () => ({
    newPlatformUrl: NEW_PLATFORM_URL,
    newPlatformLabel: NEW_PLATFORM_LABEL,
    supportPhoneLabel: SUPPORT_PHONE_LABEL,
    supportWhatsappUrl: SUPPORT_WHATSAPP_URL,
    benefits: [
      { icon: "mdi-speedometer", label: "Mais desempenho" },
      { icon: "mdi-shield-check-outline", label: "Mais segurança" },
      { icon: "mdi-trending-up", label: "Evolução contínua" },
    ],
  }),

  computed: {
    ...mapGetters(["getCurrentUserData"]),

    /** Usuário que segue com módulos nesta plataforma (bloco "Gestão"). */
    keepsProjects() {
      return hasLegacyFeature(this.getCurrentUserData?.permissions);
    },

    copy() {
      if (this.keepsProjects) {
        return {
          title: "Sua telemetria está em uma nova plataforma",
          credentials:
            "Acesse com o mesmo e-mail e senha que você já utiliza.",
          cta: "Acessar nova plataforma de telemetria",
          support:
            "Caso tenha alguma dúvida sobre qual plataforma utilizar ou encontre dificuldade no acesso, nosso time está disponível para ajudar.",
        };
      }

      return {
        title: "Uma nova experiência Foz está esperando por você",
        credentials:
          "Acesse utilizando o mesmo e-mail e senha que você já utiliza hoje.",
        cta: "Acessar nova plataforma",
        support:
          "Nosso time está disponível para auxiliar no acesso ou esclarecer qualquer dúvida sobre a nova plataforma.",
      };
    },
  },
};
</script>

<style scoped>
.migration-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  padding: 24px;
}

.migration-card {
  width: 100%;
  max-width: 600px;
  border-radius: 16px;
  overflow: hidden;
}

.migration-card__banner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 24px;
  background: linear-gradient(to top, #1d8aff 0%, #42a5f5 100%);
}

.migration-card__logo {
  max-width: 140px;
}

.migration-card__body {
  padding: 28px 32px 24px !important;
  text-align: center;
}

.migration-card__chip {
  margin-bottom: 16px;
  font-weight: 600;
}

.migration-card__title {
  margin-bottom: 16px;
  color: #1f2933;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.migration-card__text {
  margin-bottom: 12px;
  color: #52606d;
  font-size: 15px;
  line-height: 1.6;
}

.migration-card__text strong {
  color: #3e4c59;
  font-weight: 600;
}

.migration-card__benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}

.migration-card__benefit {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  background: #f2f7ff;
  color: #1f2933;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.migration-card__credentials {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0 0;
  padding: 12px 16px;
  border: 1px dashed #bcd8ff;
  border-radius: 10px;
  color: #3e4c59;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
}

.migration-card__cta {
  margin-top: 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: none;
}

.migration-card__link {
  display: block;
  margin-top: 14px;
  color: #1d8aff;
  font-size: 13px;
  word-break: break-all;
}

.migration-card__stays {
  padding: 22px 32px;
  text-align: center;
}

.migration-card__stays-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: #1f2933;
  font-size: 16px;
  font-weight: 700;
}

.migration-card__stays-text {
  margin-bottom: 16px;
  color: #52606d;
  font-size: 14px;
  line-height: 1.6;
}

.migration-card__stays-text strong {
  color: #3e4c59;
  font-weight: 600;
}

.migration-card__split {
  margin: 0;
  padding: 0;
  list-style: none;
}

.migration-card__split-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7f9fc;
  font-size: 13px;
}

.migration-card__split-row + .migration-card__split-row {
  margin-top: 8px;
}

.migration-card__split-from {
  color: #3e4c59;
  font-weight: 600;
}

.migration-card__split-to {
  padding: 3px 10px;
  border-radius: 8px;
  font-weight: 600;
}

.migration-card__split-to--new {
  background: #e8f2ff;
  color: #1d8aff;
}

.migration-card__split-to--here {
  background: #e6f4ea;
  color: #2f855a;
}

.migration-card__support {
  padding: 20px 32px 24px;
  background: #f7f9fc;
  text-align: center;
}

.migration-card__support-title {
  margin-bottom: 6px;
  color: #1f2933;
  font-size: 15px;
  font-weight: 700;
}

.migration-card__support-text {
  margin-bottom: 12px;
  color: #7b8794;
  font-size: 13px;
  line-height: 1.5;
}

.migration-card__support-phone {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #3e4c59;
  font-size: 14px;
}

.migration-card__support-cta {
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: none;
}

@media (max-width: 600px) {
  .migration-card__body {
    padding: 24px 20px 20px !important;
  }

  .migration-card__stays,
  .migration-card__support {
    padding-right: 20px;
    padding-left: 20px;
  }

  .migration-card__title {
    font-size: 19px;
  }

  .migration-card__benefit {
    flex: 1 1 100%;
    justify-content: center;
  }
}
</style>
